import { BaseFileInput } from "components/DesignSystem/BaseFileInput/BaseFileInput";
import { Button } from "components/DesignSystem/Button/Button";
import { FileIcon } from "components/design/FileIcon";
import { Preview } from "components/PreviewModal";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  useDeleteInvoiceAttachmentMutation,
  useUploadInvoiceAttachmentMutation,
} from "store/apis/invoices";
import { Invoice } from "types/Models/invoice";
import { BackendError } from "types/utils/error";

type Props = {
  invoice: Invoice;
  invoiceIdFromProp?: string;
  readOnly?: boolean;
};

export const InvoiceAttachments = ({
  invoice,
  invoiceIdFromProp,
  readOnly = false,
}: Props) => {
  const group = useCurrentGroupContext();
  const { invoiceId: invoiceIdFromParam } = useParams<{ invoiceId: string }>();
  const entityId = useCurrentEntityId();
  const { alertToast } = useToast();
  const previewModal = useModal();
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);

  const invoiceId = invoiceIdFromProp || invoiceIdFromParam;

  const [uploadAttachment, { isLoading: isUploading }] =
    useUploadInvoiceAttachmentMutation();
  const [deleteAttachment] = useDeleteInvoiceAttachmentMutation();

  const attachments = (invoice.attachments ?? []).map((f) => ({
    uuid: f.uuid,
    name: f.name,
    file_type: f.file_type,
    is_previewable: f.is_previewable,
    invoice_attachment_id: f.invoice_attachment_id,
  }));

  const handleDrop = async (files: File[]) => {
    if (!group.uuid || !entityId) return;

    await Promise.allSettled(
      files.map((file) =>
        uploadAttachment({
          groupId: group.uuid,
          entityId: entityId!,
          invoiceId,
          file,
        }).unwrap()
      )
    ).then((results) => {
      results.forEach((result) => {
        if (result.status === "rejected") {
          alertToast({
            message: (result.reason as BackendError)?.data?.error?.message,
          });
        }
      });
    });
  };

  const handleDelete = async (
    _e: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    const file = attachments[index];
    if (!file.invoice_attachment_id) return;
    if (!group.uuid || !entityId) return;

    setDeletingUuid(file.uuid);
    try {
      await deleteAttachment({
        groupId: group.uuid,
        entityId: entityId!,
        invoiceId,
        attachmentId: file.invoice_attachment_id,
      }).unwrap();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    } finally {
      setDeletingUuid(null);
    }
  };

  const handleFileClick = ({
    uuid,
  }: {
    e: React.MouseEvent<HTMLButtonElement>;
    uuid: string;
  }) => {
    setPreviewId(uuid);
    previewModal.open();
  };

  const hasDeletableAttachment = attachments.some(
    (f) => f.invoice_attachment_id
  );

  if (readOnly) {
    if (attachments.length === 0) return null;

    return (
      <>
        <div className="t-flex t-flex-col t-gap-0.5 t-border t-border-solid t-border-neutral-10 t-rounded t-px-3 t-py-2 t-bg-surface">
          {attachments.map(({ file_type, name, uuid }) => (
            <Button
              key={uuid}
              type="button"
              customType="ghost"
              onClick={(e) => handleFileClick({ e, uuid })}
              className="!t-justify-start !t-gap-2 !t-px-1"
            >
              <FileIcon fileType={file_type} size="24" className="t-shrink-0" />
              <span className="t-truncate t-text-body t-text-text-60">
                {name}
              </span>
            </Button>
          ))}
        </div>
        {previewId && (
          <Preview
            showModal={previewModal.isOpen}
            closeModal={() => {
              previewModal.close();
              setPreviewId(null);
            }}
            previewId={previewId}
          />
        )}
      </>
    );
  }

  return (
    <>
      <BaseFileInput
        multiple
        files={attachments}
        onDrop={handleDrop}
        isUploading={isUploading}
        onDelete={hasDeletableAttachment ? handleDelete : undefined}
        fileDeletingUuid={deletingUuid ?? undefined}
        onFileClick={handleFileClick}
        block
      />
      {previewId && (
        <Preview
          showModal={previewModal.isOpen}
          closeModal={() => {
            previewModal.close();
            setPreviewId(null);
          }}
          previewId={previewId}
        />
      )}
    </>
  );
};
